/**
 * Schema validation utilities inspired by Kubernetes API versioning
 * Keeps it simple - just validates and provides basic migration hints
 */

export const CURRENT_API_VERSION = "v1";
export const SUPPORTED_API_VERSIONS = ["v1"];

export interface ValidationResult {
  isValid: boolean;
  version: string | null;
  kind: string | null;
  errors: string[];
  warnings: string[];
  needsMigration: boolean;
}

/**
 * Validate imported map/template data
 */
export function validateMapSchema(data: any): ValidationResult {
  const result: ValidationResult = {
    isValid: true,
    version: data.apiVersion || null,
    kind: data.kind || null,
    errors: [],
    warnings: [],
    needsMigration: false,
  };

  // Check if this is legacy data (no apiVersion)
  if (!data.apiVersion) {
    result.warnings.push(
      "Legacy format detected (no apiVersion). Treating as v1."
    );
    result.version = "v1"; // Assume v1 for legacy data
    result.needsMigration = false; // Already compatible
  }

  // Check if kind is present
  if (!data.kind) {
    result.warnings.push(
      "No 'kind' field found. Assuming 'MapTemplate'."
    );
    result.kind = "MapTemplate";
  }

  // Validate kind
  if (data.kind && data.kind !== "MapTemplate") {
    result.errors.push(
      `Unsupported kind: ${data.kind}. Expected 'MapTemplate'.`
    );
    result.isValid = false;
  }

  // Check if version is supported
  if (data.apiVersion && !SUPPORTED_API_VERSIONS.includes(data.apiVersion)) {
    result.errors.push(
      `Unsupported API version: ${data.apiVersion}. Supported versions: ${SUPPORTED_API_VERSIONS.join(", ")}`
    );
    result.isValid = false;
  }

  // Validate required fields
  if (!data.name) {
    result.errors.push("Missing required field: name");
    result.isValid = false;
  }

  if (!data.states || !Array.isArray(data.states)) {
    result.errors.push("Missing or invalid 'states' array");
    result.isValid = false;
  }

  return result;
}

/**
 * Get a human-readable validation message
 */
export function getValidationMessage(result: ValidationResult): string {
  if (!result.isValid) {
    return `Validation failed:\n${result.errors.join("\n")}`;
  }

  if (result.warnings.length > 0) {
    return `Import successful with warnings:\n${result.warnings.join("\n")}`;
  }

  return `Valid ${result.kind} (${result.version})`;
}

/**
 * Future: Add migration functions when schema changes
 * Example:
 * 
 * export function migrateV1ToV2(data: any): any {
 *   return {
 *     ...data,
 *     apiVersion: "v2",
 *     // Add new required fields
 *     // Transform data structure
 *   };
 * }
 */

